<template>
  <form class="mx-auto">
    <FormHeader :title="title" @cancel="onCancel" @submit="onSubmit"/>
    <div class="space-y-12 mt-5">
      <div class="border-b border-gray-900/10 pb-12">
        <FormTextField v-model="fileId" field-path="attributes.fileId" :field-label="$gettext('Numéro de dossier')" :field-attrs="fileIdAttrs" :errors="errors" />
        <FormTextField v-model="commission" field-path="attributes.commission" field-label="Commission" :field-attrs="commissionAttrs" :errors="errors" />
        <FormSelectField v-model="commissionTypeId" field-path="relationships.commissionType.data.id" :field-label="$gettext('Type de commission')" :field-attrs="commissionTypeIdAttrs" :errors="errors" :options="commissionTypeOptions"/>
        <FormDateField v-model="endAt" field-path="attributes.endAt" field-label="Date de fin" :field-attrs="endAtAttrs" :errors="errors"/>
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <div class="sm:col-span-3">
            <FormComboboxField v-model="notaryId" field-path="relationships.notary.data.id" :field-label="$gettext('Notaire')" :field-attrs="notaryIdAttrs" :errors="errors" :options="notaryOptions" />
            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
              <div></div>
              <button type="button" class="w-fit rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500" @click="showCreateNotaryModal = true">
                + {{ $gettext("Créer un notaire") }}
              </button>
            </div>
          </div>
        </div>
        <CreateNotaryModal
          :open="showCreateNotaryModal"
          @close="showCreateNotaryModal = false"
          @created="onNotaryCreated"
        />
        <FormCustomerComboboxField v-model="saleListingAgreementSellers" field-path="relationships.saleListingAgreementSellers.data" :field-label="$gettext('Vendeur(s)')" :field-attrs="saleListingAgreementSellersAttrs" :errors="errors"/>
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <label for="relationships.saleListingAgreementProperties.data" class="block text-sm/6 font-medium text-gray-900 sm:pt-1.5">Propriétés listées</label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <SaleListingAgreementPropertyForm
              v-for="(field, index) in saleListingAgreementProperties"
              :key="field.value.id || field.value._tempKey"
              v-model="field.value"
              :index="index"
              :errors="errorsFor(index)"
              @remove="removeListedProperty(index)"
            />
            <button @click.prevent="addListedProperty" class="text-indigo-600 hover:text-indigo-900 transition-transform duration-200 hover:scale-110">
              <PlusCircleIcon class="size-7" aria-hidden="true" />
            </button>
            <p v-if="errors['relationships.saleListingAgreementProperties.data']" class="text-sm text-red-600">{{ errors['relationships.saleListingAgreementProperties.data'] }}</p>
          </div>
        </div>
        <div class="border-b border-gray-900/10 pb-12">
          <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
          <FormDocumentsField
            v-model="saleListingAgreementDocuments"
            category-name="saleListingAgreement"
            field-path="relationships.saleListingAgreementDocuments.data"
            :field-label="$gettext('Mission de vente')"
            field-description=""
            :field-attrs="saleListingAgreementDocumentsAttrs" :errors="errors"/>
          <FormDocumentsField
            v-model="saleListingAgreementDocuments"
            category-name="identityDocuments"
            field-path="relationships.saleListingAgreementDocuments.data"
            :field-label="$gettext('Documents d\'identité')"
            field-description=""
            :field-attrs="saleListingAgreementDocumentsAttrs" :errors="errors"/>
        </div>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>

<script setup>
import { useForm, useFieldArray } from "vee-validate";
import * as yup from "yup";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import FormDateField from "@/components/form-fields/FormDateField.vue";
import FormComboboxField from "../form-fields/FormComboboxField.vue";
import { computed, ref, watch } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import { useGettext } from "vue3-gettext";
import FormCustomerComboboxField from "@/components/form-fields/FormCustomerComboboxField.vue";
import { useNotaryStore } from "@/stores/notary";
import FormSelectField from "@/components/form-fields/FormSelectField.vue";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import FormDocumentsField from "../form-fields/FormDocumentsField.vue";
import { useCommissionTypeStore } from "@/stores/commissionType";
import { usePropertyTypeStore } from "@/stores/propertyType";
import SaleListingAgreementPropertyForm from "./SaleListingAgreementPropertyForm.vue";
import CreateNotaryModal from "@/components/notaries/CreateNotaryModal.vue";

const { $gettext } = useGettext();
const notaryStore = useNotaryStore();
const commissionTypeStore = useCommissionTypeStore();
const propertyTypeStore = usePropertyTypeStore();

const props = defineProps(["saleListingAgreement", "title"]);
const emits = defineEmits(["submit", "cancel"]);

const typeRequiresUnitIdentifier = (typeId) =>
  propertyTypeStore.all.find((t) => t.id === typeId)?.attributes?.requiresUnitIdentifier === true;

const validationSchema = yup.object({
  attributes: yup.object({
    commission: yup.number().typeError($gettext("doit être un nombre")).required($gettext("est obligatoire")),
    endAt: yup.date().required($gettext("est obligatoire"))
  }),
  relationships: yup.object({
    saleListingAgreementSellers: yup.object({
      data: yup.array().required($gettext("au moins 1 obligatoire")).of(yup.object({
        id: yup.string()
      })).min(1, $gettext("au moins 1 obligatoire"))
    }),
    saleListingAgreementProperties: yup.object({
      data: yup
        .array()
        .required($gettext("est obligatoire"))
        .of(
          yup.object({
            relationships: yup.object({
              property: yup.object({
                data: yup
                  .object()
                  .required($gettext("est obligatoire"))
                  .test("has-id", $gettext("est obligatoire"), (val) => !!val?.id)
                  .test(
                    "apartment-number-required",
                    $gettext("Numéro d'unité requis pour ce type de bien (appartement, garage, parking, cave, bureau)"),
                    (val) => {
                      if (!val) return true;
                      const typeId = val?.relationships?.propertyType?.data?.id;
                      if (!typeRequiresUnitIdentifier(typeId)) return true;
                      const apt = val?.attributes?.addressApartmentNumber;
                      return !!(apt && String(apt).trim());
                    }
                  ),
              }),
            }),
            attributes: yup.object({
              listingPrice: yup
                .number()
                .typeError($gettext("doit être un nombre"))
                .positive($gettext("doit être positif"))
                .required($gettext("est obligatoire")),
            }),
          })
        )
        .min(1, $gettext("au moins 1 obligatoire")),
    }),
  })
});

const { errors, handleSubmit, defineField, setValues } = useForm({
  validationSchema: validationSchema,
  initialValues: props.saleListingAgreement
});

const [fileId, fileIdAttrs] = defineField("attributes.fileId");
const [commission, commissionAttrs] = defineField("attributes.commission");
const [commissionTypeId, commissionTypeIdAttrs] = defineField("relationships.commissionType.data.id");
const [saleListingAgreementSellers, saleListingAgreementSellersAttrs] = defineField("relationships.saleListingAgreementSellers.data");
const { fields: saleListingAgreementProperties, push, remove } = useFieldArray("relationships.saleListingAgreementProperties.data");
const [endAt, endAtAttrs] = defineField("attributes.endAt");
const [notaryId, notaryIdAttrs] = defineField("relationships.notary.data.id");
const [saleListingAgreementDocuments, saleListingAgreementDocumentsAttrs] = defineField("relationships.saleListingAgreementDocuments.data");

const showCreateNotaryModal = ref(false);

const notaryOptions = computed(() =>
  notaryStore.sorted.map((notary) => ({
    value: notary.id,
    label: "(" + notary.attributes.zipcode + ") " + notary.attributes.name,
  }))
);

const onNotaryCreated = (created) => {
  if (created?.id) notaryId.value = created.id;
};

const commissionTypeOptions = computed(() =>
  commissionTypeStore.all.map((commissionType) => ({
    value: commissionType.id,
    label: commissionType.attributes.name,
  }))
);

const stripCustomerObjects = (customers) => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(({ id, type }) => ({ id, type }));
};

const onFormSubmit = ((values, actions) => {
  const payload = {
    ...values,
    relationships: {
      ...values.relationships,
      saleListingAgreementSellers: {
        ...values.relationships?.saleListingAgreementSellers,
        data: stripCustomerObjects(values.relationships?.saleListingAgreementSellers?.data)
      }
    }
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

const removeListedProperty = (index) => remove(index);

const addListedProperty = () => {
  push({
    _tempKey: crypto.randomUUID(),
    id: null,
    type: "saleListingAgreementProperty",
    attributes: { listingPrice: null },
    relationships: { property: { data: { id: null, attributes: { fullAddress: null } } } }
  });
};

const errorsFor = (index) => {
  const prefix = `relationships.saleListingAgreementProperties.data[${index}].`;
  return Object.fromEntries(
    Object.entries(errors.value).flatMap(([key, val]) => {
      if (key.startsWith(prefix)) {
        let error = [[key.slice(prefix.length), val]];
        return error;
      }
      return [];
    })
  );
};

watch(
  () => props.saleListingAgreement,
  (newVal) => {
    if (newVal) {
      setValues(newVal);
    }
  },
  { immediate: true }
);
</script>
